/**
 * Convert array to object
 *
 * @param {array} array Input array to convert
 * @param {string} key Key to use as identifier
 * @returns {object}
 */
function convertToObject(array, key = 'id') {
  const entries = (array || [])
    .map((item, index) => [
      item[key] || index,
      item,
    ]);

  return Object
    .fromEntries(entries);
}

function shuffle(array) {
  const result = array.slice();

  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }

  return result;
}

function sort(property) {
  let sortOrder = 1;

  if (property[0] === '-') {
    sortOrder = -1;
    property = property.substr(1);
  }

  return function (a, b) {
    let aProp = a[property];
    let bProp = b[property];

    // Función para detectar y convertir strings numéricos
    const convertIfNumeric = (value) => {
      if (typeof value === 'string') {
        // Verificar si el string es un número (incluyendo decimales y negativos)
        if (/^-?\d+(\.\d+)?$/.test(value.trim())) {
          return parseFloat(value);
        }
      }
      return value;
    };

    aProp = convertIfNumeric(aProp);
    bProp = convertIfNumeric(bProp);

    // Comparación numérica si ambos son números
    if (typeof aProp === 'number' && typeof bProp === 'number') {
      return (aProp - bProp) * sortOrder;
    }

    // Comparación de strings para otros casos
    const aStr = (aProp || '').toString();
    const bStr = (bProp || '').toString();

    return aStr.localeCompare(bStr) * sortOrder;
  };
}


function dynamicSort() {
  const props = arguments;
  const numberOfProperties = arguments.length;

  if (numberOfProperties === 1) {
    return sort(props[0]);
  }

  return function (obj1, obj2) {
    let i = 0, result = 0;

    while (result === 0 && i < numberOfProperties) {
      result = sort(props[i])(obj1, obj2);
      i++;
    }
    return result;
  };
}

/**
 * Turn a flat list of nodes into a nested tree. Each returned node is a copy of
 * the original with a `children` array (empty for leaves). Nodes whose parent is
 * not present in the list are treated as roots, so it works both when the root's
 * parent is `0` and when it points to an id outside the list (e.g. a company
 * id). Orphaned cycles are dropped rather than looping forever.
 *
 * @param {array} array Flat list of nodes
 * @param {object} [options]
 * @param {string} [options.idKey] Identifier key (default 'id')
 * @param {string} [options.parentKey] Parent reference key (default 'parent')
 * @param {string} [options.childrenKey] Key to store children under (default 'children')
 * @param {(function|string)} [options.sort] Comparator or property name used to
 *   order siblings within each level
 * @returns {array} Nested roots
 */
function buildTree(array = [], options = {}) {
  const {
    idKey = 'id',
    parentKey = 'parent',
    childrenKey = 'children',
    sort: sortBy,
  } = options;

  const nodes = Array.isArray(array) ? array : [];
  const ids = new Set(nodes.map((node) => String(node[idKey])));
  const comparator = typeof sortBy === 'function'
    ? sortBy
    : sortBy
      ? dynamicSort(sortBy)
      : null;

  const ROOT = Symbol('root');
  const byParent = new Map();

  nodes.forEach((node) => {
    const parentValue = String(node[parentKey]);
    const key = ids.has(parentValue) ? parentValue : ROOT;

    if (!byParent.has(key)) {
      byParent.set(key, []);
    }

    byParent.get(key).push(node);
  });

  const build = (key) => {
    const siblings = (byParent.get(key) || []).slice();

    if (comparator) {
      siblings.sort(comparator);
    }

    return siblings.map((node) => ({
      ...node,
      [childrenKey]: build(String(node[idKey])),
    }));
  };

  return build(ROOT);
}

function groupBy(values, keyFinder) {
  return values.reduce((a, b) => {
    const key = typeof keyFinder === 'function'
      ? keyFinder(b)
      : b[keyFinder];

    a[key] = (!a[key]) ? [b] : [...a[key], b];

    return a;
  }, {});
};

/**
 * Filter array by object
 *
 * @param {object} item
 * @param {object} params
 * @returns {array}
 */
function multiFilter(item, params = {}) {
  return !Object
    .entries(params)
    .map(([param, value]) => Array.isArray(value)
      ? value.includes(item[param])
      : item[param] === value)
    .includes(false);
}

/**
 * Paginate array
 *
 * @param {array} array
 * @param {number} pageSize
 * @param {number} page
 * @returns {array}
 */
function paginate(array, pageSize, page) {
  const size = pageSize || array.length;

  return page
    ? array.slice((page - 1) * size, page * size)
    : new Array(Math.ceil(array.length / pageSize))
      .fill(null)
      .map((_, index) => paginate(array, pageSize, index + 1));
}

export {
  buildTree,
  convertToObject,
  dynamicSort,
  groupBy,
  multiFilter,
  paginate,
  shuffle,
};
